/**
 * Asset Dashboard Page
 * ====================
 * Overview of IT assets with stats and quick actions.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Server,
  MapPin,
  Building2,
  Search,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Wifi,
} from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import AddByIPModal from '@/components/AddByIPModal'
import { useAssets, useAssetStats } from '@/hooks/useAssets'
import { useAssetLocations } from '@/hooks/useAssetLocations'
import { useAssetManufacturers } from '@/hooks/useAssetManufacturers'
import { ASSET_STATUS_COLORS, ASSET_STATUS_LABELS, ASSET_TYPE_LABELS } from '@/types'
import type { AssetType } from '@/types'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  loading?: boolean
  to?: string
  color?: string
}

function StatCard({ title, value, icon, loading, to, color }: StatCardProps) {
  const colorClasses = color || 'bg-primary-50 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400'

  const content = (
    <CardContent className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorClasses}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {loading ? '...' : value}
        </p>
      </div>
    </CardContent>
  )

  if (to) {
    return (
      <Link to={to} className="block">
        <Card className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
          {content}
        </Card>
      </Link>
    )
  }

  return <Card>{content}</Card>
}

export default function AssetDashboard() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showAddByIPModal, setShowAddByIPModal] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const { data: statsData, isLoading: statsLoading } = useAssetStats()
  const { data: locationsData, isLoading: locationsLoading } = useAssetLocations()
  const { data: manufacturersData, isLoading: manufacturersLoading } = useAssetManufacturers()

  // Search for autocomplete results (only when 2+ characters)
  const searchTerm = searchQuery.trim()
  const { data: searchResults, isLoading: searchLoading } = useAssets(
    {
      search: searchTerm,
      limit: 8,
    },
    { enabled: searchTerm.length >= 2 }
  )

  const stats = statsData?.data
  const totalAssets = stats?.total_count ?? 0
  const totalLocations = locationsData?.data?.length ?? 0
  const totalManufacturers = manufacturersData?.data?.length ?? 0

  const suggestions = searchTerm.length >= 2 ? (searchResults?.data ?? []) : []

  // Status counts from stats
  const onlineCount = stats?.by_status?.online ?? 0
  const offlineCount = stats?.by_status?.offline ?? 0
  const degradedCount = stats?.by_status?.degraded ?? 0
  const unknownCount = stats?.by_status?.unknown ?? 0

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setShowDropdown(false)
      navigate(`/assets/list?search=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setShowDropdown(true)
    setSelectedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      const asset = suggestions[selectedIndex]
      setShowDropdown(false)
      navigate(`/assets/${asset.asset_id}`)
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleSuggestionClick = (assetId: string) => {
    setShowDropdown(false)
    navigate(`/assets/${assetId}`)
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Asset Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400">
            IT infrastructure overview
          </p>
        </div>
        <Link to="/assets/new">
          <Button icon={<Plus size={16} />}>Add Asset</Button>
        </Link>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1" ref={searchRef}>
              <div className="relative">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search assets by name, IP, service tag..."
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-12 pr-4 py-3 border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
                {searchLoading && searchTerm.length >= 2 && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                {/* Autocomplete dropdown */}
                {showDropdown && searchTerm.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                    {suggestions.length === 0 && !searchLoading ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No assets found for "{searchQuery}"
                      </div>
                    ) : (
                      suggestions.map((asset, index) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => handleSuggestionClick(asset.asset_id)}
                          className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            index === selectedIndex ? 'bg-primary-50 dark:bg-primary-900/50' : ''
                          } ${index !== suggestions.length - 1 ? 'border-b dark:border-gray-700' : ''}`}
                        >
                          <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <Server className="text-gray-400" size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {asset.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {asset.asset_id}
                              {asset.ip_address && ` • ${asset.ip_address}`}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${ASSET_STATUS_COLORS[asset.status]}`}>
                            {ASSET_STATUS_LABELS[asset.status]}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button type="submit" size="lg">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Assets"
          value={totalAssets}
          icon={<Server size={24} />}
          loading={statsLoading}
          to="/assets/list"
        />
        <StatCard
          title="Online"
          value={onlineCount}
          icon={<CheckCircle size={24} />}
          loading={statsLoading}
          to="/assets/list?status=online"
          color="bg-green-50 text-green-600 dark:bg-green-900/50 dark:text-green-400"
        />
        <StatCard
          title="Offline"
          value={offlineCount}
          icon={<XCircle size={24} />}
          loading={statsLoading}
          to="/assets/list?status=offline"
          color="bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-400"
        />
        <StatCard
          title="Degraded"
          value={degradedCount}
          icon={<AlertTriangle size={24} />}
          loading={statsLoading}
          to="/assets/list?status=degraded"
          color="bg-yellow-50 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400"
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Locations"
          value={totalLocations}
          icon={<MapPin size={24} />}
          loading={locationsLoading}
          to="/assets/locations"
        />
        <StatCard
          title="Manufacturers"
          value={totalManufacturers}
          icon={<Building2 size={24} />}
          loading={manufacturersLoading}
          to="/assets/manufacturers"
        />
        <StatCard
          title="Unknown Status"
          value={unknownCount}
          icon={<HelpCircle size={24} />}
          loading={statsLoading}
          to="/assets/list?status=unknown"
          color="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
        />
      </div>

      {/* Breakdown by type */}
      {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
        <Card>
          <CardHeader title="Assets by Type" />
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.by_type).map(([type, count]) => (
                <Link
                  key={type}
                  to={`/assets/list?asset_type=${type}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">
                    {ASSET_TYPE_LABELS[type as AssetType] || type}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breakdown by location */}
      {stats?.by_location && Object.keys(stats.by_location).length > 0 && (
        <Card>
          <CardHeader title="Assets by Location" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(stats.by_location).map(([locationName, count]) => (
                <div
                  key={locationName}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-gray-700 dark:text-gray-300">{locationName}</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <Card>
        <CardHeader title="Quick Actions" />
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Link to="/assets/new">
              <Button icon={<Plus size={16} />}>Add New Asset</Button>
            </Link>
            <Button
              variant="secondary"
              icon={<Wifi size={16} />}
              onClick={() => setShowAddByIPModal(true)}
            >
              Add by IP
            </Button>
            <Link to="/assets/list">
              <Button variant="secondary" icon={<Server size={16} />}>
                View All Assets
              </Button>
            </Link>
            <Link to="/assets/locations">
              <Button variant="secondary" icon={<MapPin size={16} />}>
                Manage Locations
              </Button>
            </Link>
            <Link to="/assets/manufacturers">
              <Button variant="secondary" icon={<Building2 size={16} />}>
                Manage Manufacturers
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Add by IP Modal */}
      <AddByIPModal
        isOpen={showAddByIPModal}
        onClose={() => setShowAddByIPModal(false)}
      />
    </div>
  )
}
